import { PlayIcon, XMarkIcon } from '@heroicons/react/24/outline'
import cloneDeep from 'clone-deep'
import { LayoutLoading } from 'components/LayoutLoading'
import { SaveChanges } from 'components/SaveChanges'
import { InputType } from 'config'
import { useEffect, useMemo, useState } from 'react'
import { toast } from 'react-toastify'
import {
  getLoanTagSuggestions,
  getServicingWorkflowConfig,
  runServicingWorkflow,
  saveServicingWorkflowConfig,
} from 'services'
import { Button } from 'stories/components'
import { InputConvert, InputValidate } from 'utils'
import { RenderInput } from 'utils/RenderInput'

interface ServicingWorkflowConfig {
  enabled: boolean
  defaultDays: number
  accelerationDays: number
  referralDays: number
  insuranceExpiringDays: number
  reserveLowThreshold: number
  staleDrawDays: number
  paymentDefaultTracking: boolean
  paymentDefaultDays: number
  promiseToPayGraceDays: number
  exceptionTags: string[]
}

// Grouped the way a servicer thinks about them rather than by data type: what the scan does at all,
// then the delinquency ladder, then everything else it watches.
const groups: Array<{ title: string; description: string; keys: string[] }> = [
  {
    title: 'Delinquency',
    description:
      'Days past due, counted the same way the late charge counts them, so a loan shown in default here is one the interest maths also treats as in default.',
    keys: ['defaultDays', 'accelerationDays', 'referralDays'],
  },
  {
    title: 'First and second payment defaults',
    description:
      'A missed first or second payment is tracked separately from the ladder above and listed under Servicing > Payment Defaults, because collections works those files first. The record is kept after the borrower catches up.',
    keys: ['paymentDefaultDays'],
  },
  {
    title: 'Insurance, reserve and draws',
    description: 'The rest of what the nightly scan watches on each serviced loan.',
    keys: ['insuranceExpiringDays', 'reserveLowThreshold', 'staleDrawDays'],
  },
  {
    title: 'Collections',
    description:
      'How long a borrower has after the date they promised before the promise is recorded as broken.',
    keys: ['promiseToPayGraceDays'],
  },
]

const configInputs = (): Record<string, InputType> => ({
  defaultDays: {
    inputType: 'text',
    type: 'number',
    title: 'Days past due before a loan is in default',
    required: true,
  },
  accelerationDays: {
    inputType: 'text',
    type: 'number',
    title: 'Days past due before acceleration',
    required: true,
  },
  referralDays: {
    inputType: 'text',
    type: 'number',
    title: 'Days past due before legal referral',
    required: true,
  },
  paymentDefaultDays: {
    inputType: 'text',
    type: 'number',
    title: 'Days past due before a 1st/2nd payment default is recorded',
    tooltip:
      'Counted past the loan\'s grace period, the same way the late charge counts. 1 flags it as soon as it is late.',
    required: true,
  },
  insuranceExpiringDays: {
    inputType: 'text',
    type: 'number',
    title: 'Warn this many days before a policy expires',
    required: true,
  },
  reserveLowThreshold: {
    inputType: 'text',
    type: 'number',
    title: 'Warn when the construction reserve falls to',
    prefix: '$',
    required: true,
  },
  staleDrawDays: {
    inputType: 'text',
    type: 'number',
    title: 'Days an open draw request may sit',
    tooltip: 'Set to 0 to stop checking for stale draw requests.',
    required: true,
  },
  promiseToPayGraceDays: {
    inputType: 'text',
    type: 'number',
    title: 'Days of grace on a promise to pay',
    tooltip:
      'Counted after the promised date. Grace covers a payment the borrower sent on time that posts a day or two later. Set to 0 to break a promise the morning after.',
    required: true,
  },
})

export const ServicingWorkflowConfiguration = () => {
  const [isLoading, setLoading] = useState(true)
  const [isRunning, setRunning] = useState(false)
  const [config, setConfig] = useState<ServicingWorkflowConfig | null>(null)
  const [inputs, setInputs] = useState<Record<string, InputType>>(configInputs())
  const [isChanged, setChanged] = useState(false)
  const [tagInput, setTagInput] = useState('')
  const [tagSuggestions, setTagSuggestions] = useState<string[]>([])
  const [lastRun, setLastRun] = useState<string>('')

  useEffect(() => {
    load()
    getLoanTagSuggestions()
      .then((tags) => setTagSuggestions(Array.isArray(tags) ? tags : []))
      .catch(() => setTagSuggestions([]))
  }, [])

  const load = async () => {
    setLoading(true)
    try {
      const data = await getServicingWorkflowConfig()
      setConfig(data)
      const temp = configInputs()
      Object.keys(temp).forEach((key) => (temp[key].value = (data as any)?.[key]))
      setInputs(temp)
    } finally {
      setLoading(false)
    }
  }

  const onChange = (key: string, value: any) => {
    const temp = cloneDeep(inputs)
    temp[key].value = InputConvert(temp[key], value)
    temp[key].error = InputValidate(temp[key])
    setInputs(temp)
    setChanged(true)
  }

  const setFlag = (key: 'enabled' | 'paymentDefaultTracking', value: boolean) => {
    setConfig({ ...(config as ServicingWorkflowConfig), [key]: value })
    setChanged(true)
  }

  const exceptionTags = config?.exceptionTags || []

  const addTag = (tag: string) => {
    const value = tag.trim()
    setTagInput('')
    if (!value) return
    if (exceptionTags.some((t) => t.toLowerCase() === value.toLowerCase())) return
    setConfig({ ...(config as ServicingWorkflowConfig), exceptionTags: [...exceptionTags, value] })
    setChanged(true)
  }

  const removeTag = (tag: string) => {
    setConfig({
      ...(config as ServicingWorkflowConfig),
      exceptionTags: exceptionTags.filter((t) => t !== tag),
    })
    setChanged(true)
  }

  // Tags already in use elsewhere, minus the ones already on the list, so picking is faster than
  // retyping and a typo doesn't quietly create an exception that matches nothing.
  const availableSuggestions = useMemo(
    () =>
      tagSuggestions
        .filter((t) => !exceptionTags.some((e) => e.toLowerCase() === t.toLowerCase()))
        .filter((t) => !tagInput || t.toLowerCase().includes(tagInput.toLowerCase()))
        .slice(0, 12),
    [tagSuggestions, exceptionTags, tagInput],
  )

  const onSave = async () => {
    const temp = cloneDeep(inputs)
    let hasError = false
    Object.keys(temp).forEach((key) => {
      temp[key].error = InputValidate(temp[key])
      if (temp[key].error) hasError = true
    })
    setInputs(temp)
    if (hasError) return

    const data: Record<string, any> = {
      enabled: !!config?.enabled,
      paymentDefaultTracking: config?.paymentDefaultTracking !== false,
      exceptionTags,
    }
    Object.keys(temp).forEach((key) => (data[key] = Number(temp[key].value)))

    setLoading(true)
    try {
      const saved = await saveServicingWorkflowConfig(data)
      setConfig(saved)
      setChanged(false)
      toast('Servicing workflow settings have been saved.', { type: 'info' })
    } finally {
      setLoading(false)
    }
  }

  const onRunNow = async () => {
    setRunning(true)
    try {
      const result = await runServicingWorkflow()
      if (result?.skipped) {
        setLastRun(result.skipped)
      } else {
        const raised = Object.entries(result?.raised || {})
        setLastRun(
          raised.length
            ? `Scanned ${result.scanned} loans. Raised ${raised.map(([key, n]) => `${key} (${n})`).join(', ')}.`
            : `Scanned ${result.scanned} loans. Nothing new to raise.`,
        )
      }
    } catch {
      setLastRun('The scan could not be run. Try again, or check the server log.')
    } finally {
      setRunning(false)
    }
  }

  return (
    <div className="relative pt-4">
      <LayoutLoading show={isLoading || isRunning} />
      <SaveChanges show={isChanged} onSave={onSave} />

      <div className="max-w-3xl flex flex-col gap-6">
        <div>
          <h3 className="text-lg font-semibold mb-1">Servicing Workflow</h3>
          <p className="text-sm text-gray-500 dark:text-gray-400">
            Each night the platform looks at every serviced loan and raises a Servicing Action when one crosses a line
            set here. Who hears about it is configured separately, under Loan Actions, so changing a threshold does not
            disturb the emails and tasks already set up against it.
          </p>
        </div>

        <div className="border rounded p-4 flex flex-col gap-3">
          <RenderInput
            input={{
              inputType: 'toggle',
              title: 'Run the nightly scan',
              tooltip:
                'While this is off nothing is raised by the scan. Actions that fire from what a servicer does, such as a payment or a hold, are unaffected.',
              value: !!config?.enabled,
            }}
            Key="enabled"
            onChange={(_key: string, value: any) => setFlag('enabled', !!value)}
          />
          <RenderInput
            input={{
              inputType: 'toggle',
              title: 'Track first and second payment defaults',
              tooltip:
                'Independent of the scan switch above: this only records the default. Who is emailed or tasked is set under Loan Actions.',
              value: config?.paymentDefaultTracking !== false,
            }}
            Key="paymentDefaultTracking"
            onChange={(_key: string, value: any) => setFlag('paymentDefaultTracking', !!value)}
          />
          <div className="flex items-center gap-3 flex-wrap">
            <Button color="gray" onClick={onRunNow} disabled={isRunning}>
              <span className="flex items-center gap-1">
                <PlayIcon className="w-4 h-4" />
                Run now
              </span>
            </Button>
            {!!lastRun && <span className="text-sm text-gray-500 dark:text-gray-400">{lastRun}</span>}
          </div>
        </div>

        {groups.map((group) => (
          <div key={group.title}>
            <h4 className="font-semibold text-[15px]">{group.title}</h4>
            <p className="text-sm text-gray-500 dark:text-gray-400 mb-3">{group.description}</p>
            <div className="grid md:grid-cols-3 gap-4">
              {group.keys.map((key) => (
                <RenderInput key={key} input={inputs[key]} Key={key} onChange={onChange} />
              ))}
            </div>
          </div>
        ))}

        <div>
          <h4 className="font-semibold text-[15px]">Default rate</h4>
          <p className="text-sm text-gray-500 dark:text-gray-400">
            A loan that enters default takes the default rate set on its own Penalties screen, and gets its own rate
            back when it clears. Statements, ACH drafts, notices and exports quote whatever the loan is carrying, so
            this is what makes them show the default rate. A loan with no default rate set, or with default interest
            switched off, is left alone. So is a rate somebody has since edited by hand.
          </p>
        </div>

        <div>
          <h4 className="font-semibold text-[15px]">Exceptions</h4>
          <p className="text-sm text-gray-500 dark:text-gray-400 mb-3">
            A loan carrying any of these tags is left out of the delinquency ladder: no default, no acceleration, no
            referral, and no default rate. Loans on Hold are always left out. Use this for the calls your team makes
            about a particular loan, such as VIP, a pending payoff, litigation or a short sale in progress.
          </p>

          <div className="flex flex-wrap gap-2 mb-3">
            {exceptionTags.length === 0 && (
              <span className="text-sm text-gray-400 italic">No exceptions. Every delinquent loan escalates.</span>
            )}
            {exceptionTags.map((tag) => (
              <span
                key={tag}
                className="inline-flex items-center gap-1 px-2 py-1 rounded bg-slate-100 dark:bg-gray-700 text-sm"
              >
                {tag}
                <button
                  type="button"
                  aria-label={`Remove ${tag}`}
                  className="hover:text-red-600"
                  onClick={() => removeTag(tag)}
                >
                  <XMarkIcon className="w-4 h-4" />
                </button>
              </span>
            ))}
          </div>

          <input
            type="text"
            value={tagInput}
            placeholder="Add a tag and press Enter"
            className="border rounded px-3 py-2 text-sm w-full max-w-sm dark:bg-gray-800"
            onChange={(e) => setTagInput(e.target.value)}
            onKeyDown={(e) => {
              if (e.key === 'Enter') {
                e.preventDefault()
                addTag(tagInput)
              }
            }}
          />

          {availableSuggestions.length > 0 && (
            <div className="mt-2 flex flex-wrap items-center gap-2">
              <span className="text-xs uppercase tracking-wide text-gray-400">Tags in use</span>
              {availableSuggestions.map((tag) => (
                <button
                  key={tag}
                  type="button"
                  className="px-2 py-0.5 rounded border text-sm hover:border-shade-blue hover:text-shade-blue"
                  onClick={() => addTag(tag)}
                >
                  {tag}
                </button>
              ))}
            </div>
          )}
        </div>
      </div>
    </div>
  )
}
